import { useEffect, useState } from 'react';
import axios from 'axios';
import { useWeb3React } from '@web3-react/core';
import useRainbows from '../hooks/useRainbows';
import useTokenVault from './useTokenVault';

import { parseEther, ZERO } from '../util';

const SYNC_BLOCK_BUFFER =
  parseInt(process.env.NEXT_PUBLIC_SYNC_BLOCK_BUFFER) || 1;

const useRainbowBalance = () => {
  const { account } = useWeb3React();
  const rainbows = useRainbows();
  const tokenVault = useTokenVault();

  const [currentBalance, setCurrentBalance] = useState(ZERO);

  const [genesisRewards, setGenesisRewards] = useState(ZERO);
  const [companionRewards, setCompanionRewards] = useState(ZERO);
  const [evilRewards, setEvilRewards] = useState(ZERO);
  const [totalPendingBalance, setTotalPendingBalance] = useState(ZERO);

  const [withdrawals, setWithdrawals] = useState(ZERO);
  const [availablePendingBalance, setAvailablePendingBalance] = useState(ZERO);
  const [availablePendingBalanceTax, setAvailablePendingBalanceTax] =
    useState(ZERO);

  const [dailyGenesisRewards, setDailyGenesisRewards] = useState(ZERO);
  const [dailyCompanionRewards, setDailyCompanionRewards] = useState(ZERO);

  useEffect(() => {
    let ignore = false;
    setCurrentBalance(ZERO);
    if (rainbows) {
      const fetchCurrentBalance = () =>
        rainbows.balanceOf(account).then((balance) => {
          if (!ignore) setCurrentBalance(balance);
        });
      fetchCurrentBalance();
      const transferFrom = rainbows.filters.Transfer(account);
      const transferTo = rainbows.filters.Transfer(null, account);
      rainbows.on(transferFrom, fetchCurrentBalance);
      rainbows.on(transferTo, fetchCurrentBalance);
      return () => {
        rainbows.off(transferFrom, fetchCurrentBalance);
        rainbows.off(transferTo, fetchCurrentBalance);
        ignore = true;
      };
    }
  }, [account, rainbows]);

  useEffect(() => {
    let ignore = false;
    setGenesisRewards(ZERO);
    setCompanionRewards(ZERO);
    setEvilRewards(ZERO);
    setTotalPendingBalance(ZERO);

    setWithdrawals(ZERO);
    setAvailablePendingBalance(ZERO);

    setDailyGenesisRewards(ZERO);
    setDailyCompanionRewards(ZERO);
    const fetchBalance = () => {
      axios
        .get('/api/balance', {
          params: { account },
        })
        .then((res) => {
          if (!ignore) {
            const {
              noundles,
              companions,
              evilNoundles,
              total,
              withdrawals,
              available,
              daily,
            } = res.data;

            setGenesisRewards(parseEther(noundles));
            setCompanionRewards(parseEther(companions));
            setEvilRewards(parseEther(evilNoundles));
            setTotalPendingBalance(parseEther(total));

            setWithdrawals(parseEther(withdrawals));
            setAvailablePendingBalance(parseEther(available.total));
            setAvailablePendingBalanceTax(parseEther(available.tax));

            setDailyGenesisRewards(parseEther(daily.noundles));
            setDailyCompanionRewards(parseEther(daily.companions));
          }
        });
    };

    fetchBalance();

    const syncWithdrawals = () => axios.post('/api/sync/withdrawals');

    const syncAndFetch = (_recipient, _amount, _tax, _nonce, event) => {
      event
        .getTransaction()
        .then((tx) => tx.wait(SYNC_BLOCK_BUFFER + 1))
        .then(syncWithdrawals)
        .then(fetchBalance);
    };
    const withdrawal = tokenVault.filters.Withdrawal(account);
    tokenVault.on(withdrawal, syncAndFetch);
    return () => {
      tokenVault.off(withdrawal, syncAndFetch);
      ignore = true;
    };
  }, [account, tokenVault]);

  return {
    current: currentBalance,
    pending: {
      genesis: genesisRewards,
      companions: companionRewards,
      evil: evilRewards,
      total: totalPendingBalance,
      available: {
        total: availablePendingBalance,
        tax: availablePendingBalanceTax,
      },
    },
    withdrawals,
    daily: {
      genesis: dailyGenesisRewards,
      companions: dailyCompanionRewards,
    },
  };
};

export default useRainbowBalance;
